import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from './DataAccessUtils';

// How a buyer classifies a supplier in its private network. These are UI-level keys — the API has
// no single "type" field. Classification is carried by two independent facts on the supplier
// facility, which the buyer sets when adding the supplier:
//   internal team → supplier company and facility get status 'private' + privateBuyerCompanyId
//                   (the flag the whole platform already reads for internal techs)
//   landlord      → supplier_facility.is_landlord
// A supplier that is neither is a third party supplier.
export const SUPPLIER_TYPES = {
	THIRD_PARTY: 'ThirdParty',
	INTERNAL: 'Internal',
	LANDLORD: 'Landlord',
};

// Singular labels, for naming one supplier: the Add dropdown, the add modal's title, and the Type
// row on the detail page.
export const SUPPLIER_TYPE_LABELS = {
	[SUPPLIER_TYPES.THIRD_PARTY]: 'Supplier',
	[SUPPLIER_TYPES.INTERNAL]: 'Internal Team',
	[SUPPLIER_TYPES.LANDLORD]: 'Landlord',
};

// Not a classification — the tab that applies no type filter at all. Kept out of SUPPLIER_TYPES
// so it can never be mistaken for something a supplier can be, or offered in the Add dropdown.
export const ALL_SUPPLIER_TYPES = 'All';

// Each tab names a group of suppliers rather than one, so the tab strip is plural throughout.
export const SUPPLIER_TYPE_TAB_LABELS = {
	[ALL_SUPPLIER_TYPES]: 'All',
	[SUPPLIER_TYPES.INTERNAL]: 'Internal Teams',
	[SUPPLIER_TYPES.THIRD_PARTY]: 'Suppliers',
	[SUPPLIER_TYPES.LANDLORD]: 'Landlords',
};

const PRIVATE_SUPPLIER_FACILITY_STATUS = 'private';

// Query params / request body flags for a supplier type. For the three real types both flags are
// always sent so the pair is unambiguous — third party is "neither", not "unspecified", and the
// API rejects both being true. The All tab clears them instead, which is what makes "no flags"
// mean "every type" rather than defaulting to third party.
export const getSupplierTypeFlags = (supplierType) =>
	supplierType === ALL_SUPPLIER_TYPES
		? { isInternalTeam: undefined, isLandlord: undefined }
		: {
				isInternalTeam: supplierType === SUPPLIER_TYPES.INTERNAL,
				isLandlord: supplierType === SUPPLIER_TYPES.LANDLORD,
		  };

// Filter values arrive back from the URL as strings, so compare loosely.
const isFlagOn = (value) => String(value) === 'true';

// Inverse of getSupplierTypeFlags, for reading the active tab back out of the stored filters.
export const getSupplierTypeFromFlags = (filters) => {
	// nullSafeGet returns undefined for any falsey value, which would read the Suppliers tab's
	// `false` pair as "no flags set" and snap the strip back to All. These two flags distinguish
	// false from absent, so they need the variant that preserves it.
	const isInternalTeam = nullSafeGetWithZero('isInternalTeam', filters);
	const isLandlord = nullSafeGetWithZero('isLandlord', filters);
	// Absent flags are the All tab; a present-but-false pair is specifically third party.
	if (typeof isInternalTeam === 'undefined' && typeof isLandlord === 'undefined') {
		return ALL_SUPPLIER_TYPES;
	}
	if (isFlagOn(isInternalTeam)) {
		return SUPPLIER_TYPES.INTERNAL;
	}
	if (isFlagOn(isLandlord)) {
		return SUPPLIER_TYPES.LANDLORD;
	}
	return SUPPLIER_TYPES.THIRD_PARTY;
};

// Classifies a hydrated supplier facility. Used for display — the type is derived from the
// facility, never stored on the buyer supplier relationship.
export const getSupplierTypeForFacility = (supplierFacility) => {
	if (nullSafeGetOrElse('status', supplierFacility, '') === PRIVATE_SUPPLIER_FACILITY_STATUS) {
		return SUPPLIER_TYPES.INTERNAL;
	}
	if (nullSafeGet('isLandlord', supplierFacility)) {
		return SUPPLIER_TYPES.LANDLORD;
	}
	return SUPPLIER_TYPES.THIRD_PARTY;
};

export const getSupplierTypeLabelForFacility = (supplierFacility) =>
	SUPPLIER_TYPE_LABELS[getSupplierTypeForFacility(supplierFacility)];

// One add form serves all three types, so its field labels have to name the type being added —
// calling an internal team's contact "Supplier Contact Email Address" is wrong on its face. A
// landlord is name-only (nobody is invited), so it carries no contact labels.
export const SUPPLIER_TYPE_ADD_FORM_LABELS = {
	[SUPPLIER_TYPES.THIRD_PARTY]: {
		name: 'Supplier Company Name',
		namePlaceholder: "John's Plumbing Service",
		contactEmail: 'Supplier Contact Email Address',
		phoneNumbers: 'Supplier Phone Numbers',
	},
	[SUPPLIER_TYPES.INTERNAL]: {
		name: 'Internal Team Company Name',
		namePlaceholder: 'In-House Maintenance Team',
		contactEmail: 'Internal Team Contact Email Address',
		phoneNumbers: 'Internal Team Phone Numbers',
	},
	[SUPPLIER_TYPES.LANDLORD]: {
		name: 'Landlord Name',
		namePlaceholder: 'Acme Property Group',
		contactEmail: 'Landlord Contact Email Address',
		phoneNumbers: 'Landlord Phone Numbers',
	},
};

export const getAddSupplierFormLabels = (supplierType) =>
	SUPPLIER_TYPE_ADD_FORM_LABELS[supplierType] ||
	SUPPLIER_TYPE_ADD_FORM_LABELS[SUPPLIER_TYPES.THIRD_PARTY];
